import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { User, Building2, CheckCircle } from 'lucide-react';

export default function AccountTypeSelection() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center space-x-2">
          <Building2 className="h-10 w-10 text-primary-600" />
          <span className="text-2xl font-bold text-gray-900">ePeople Agent</span>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Get started with ePeople Agent
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Choose the account type that best fits your needs
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-3xl px-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Individual Account - Candidates */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-primary-300 transition-colors">
            <div className="p-8">
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-lg mb-4">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Individual Account
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Perfect for job seekers looking for opportunities
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  'Create your professional profile',
                  'Apply to multiple jobs',
                  'Track application status',
                  'View AI match scores',
                  'Get Glass Box explanations',
                  'Receive personalized recommendations',
                ].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/signup?type=individual">
                <Button className="w-full">
                  Sign up as Job Seeker
                </Button>
              </Link>

              <p className="text-center text-xs text-gray-500 mt-4">
                Free • No credit card required
              </p>
            </div>
          </div>

          {/* Organization Account */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-primary-300 hover:border-primary-400 transition-colors relative">
            <div className="absolute top-4 right-4">
              <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Popular
              </span>
            </div>
            
            <div className="p-8">
              <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-lg mb-4">
                <Building2 className="h-8 w-8 text-primary-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Organization Account
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                For companies hiring talent with AI-powered tools
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  'Post unlimited jobs',
                  'AI-powered candidate matching',
                  'Glass Box AI explanations',
                  'Team collaboration tools',
                  'ATS integrations',
                  'Compliance & reporting',
                  'Multi-user access',
                  'Advanced analytics',
                ].map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/signup?type=organization">
                <Button className="w-full bg-primary-600 hover:bg-primary-700">
                  Start Free Trial
                </Button>
              </Link>

              <p className="text-center text-xs text-gray-500 mt-4">
                14-day free trial • No credit card required
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>

        {/* Team Invitation */}
        <div className="mt-8 text-center">
          <div className="inline-block bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Joining your team?</strong>
            </p>
            <Link 
              to="/join-team" 
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Use your invitation code →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
